/**
 * Invites Storage Adapter
 *
 * Provides a unified interface for invite storage operations,
 * supporting both local (dev) and Supabase (production) backends.
 */

import { localInvitesStore } from './invites.local';
import { supaInvitesStore, hashEmail, hashToken, normalizeEmail } from './invites.supabase';
import type {
  ClientInvite,
  InviteCreateInput,
  InviteCreateResult,
  InviteRateLimitResult,
  InviteDedupeResult,
  TokenRedemptionResult,
} from '@/types/invites';

const backend = (import.meta as any).env?.VITE_APP_BACKEND || 'local';
const dual = (import.meta as any).env?.VITE_APP_DUAL_WRITE === '1';

export const invitesStore = {
  /**
   * List invites by coach
   */
  async listByCoach(coachId: string): Promise<ClientInvite[]> {
    return backend === 'supabase'
      ? await supaInvitesStore.listByCoach(coachId)
      : await localInvitesStore.listByCoach(coachId);
  },

  /**
   * List invites by email
   */
  async listByEmail(email: string): Promise<ClientInvite[]> {
    return backend === 'supabase'
      ? await supaInvitesStore.listByEmail(email)
      : await localInvitesStore.listByEmail(email);
  },

  /**
   * List invites by email hash (PII-safe)
   */
  async listByEmailHash(emailHash: string): Promise<ClientInvite[]> {
    if (backend === 'supabase') {
      return await supaInvitesStore.listByEmailHash(emailHash);
    }
    // Local store doesn't support hash lookup - fall back to empty
    return [];
  },

  /**
   * Get invite by ID
   */
  async get(id: string): Promise<ClientInvite | null> {
    return backend === 'supabase' ? await supaInvitesStore.get(id) : await localInvitesStore.get(id);
  },

  /**
   * Get invite by token hash (for redemption)
   */
  async getByTokenHash(tokenHash: string): Promise<ClientInvite | null> {
    if (backend === 'supabase') {
      return await supaInvitesStore.getByTokenHash(tokenHash);
    }
    // Local store doesn't support token lookup
    return null;
  },

  /**
   * Create new invite with secure token
   * Returns the invite, raw token, and signup URL
   */
  async create(input: InviteCreateInput): Promise<InviteCreateResult> {
    if (backend === 'supabase') {
      const result = await supaInvitesStore.create(input);
      if (dual) {
        try {
          await localInvitesStore.insert({
            ...result.invite,
            status: result.invite.status as any,
          });
        } catch {
          // OBS-11 a-ignore: dual-write mirror to the local store after the server write succeeded; the mirror is a dev aid.
        }
      }
      return result;
    } else {
      // Local mode - generate a simple token
      const token = `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const emailNormalized = normalizeEmail(input.email);
      const invite: ClientInvite = {
        id: `inv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        email: input.email,
        emailNormalized,
        clientName: input.clientName,
        coachId: input.coachId,
        orgId: input.orgId,
        status: 'pending',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
        sentCount: 1,
        lastSentAt: new Date().toISOString(),
      };
      await localInvitesStore.insert(invite as any);
      const signupUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/signup?token=${encodeURIComponent(token)}&email=${encodeURIComponent(emailNormalized)}`;
      return { invite, token, signupUrl };
    }
  },

  /**
   * Legacy insert (backward compatibility)
   * @deprecated Use create() instead for secure token handling
   */
  async insert(inv: ClientInvite): Promise<ClientInvite> {
    if (backend === 'supabase') {
      const row = await supaInvitesStore.insert(inv);
      if (dual)
        try {
          await localInvitesStore.insert(inv as any);
        } catch {
          // OBS-11 a-ignore: dual-write mirror to the local store after the server write succeeded; the mirror is a dev aid.
        }
      return row;
    } else {
      return await localInvitesStore.insert(inv as any);
    }
  },

  /**
   * Update invite
   */
  async update(id: string, patch: Partial<ClientInvite>): Promise<ClientInvite | null> {
    if (backend === 'supabase') {
      const row = await supaInvitesStore.update(id, patch as any);
      if (dual)
        try {
          await localInvitesStore.update(id, patch as any);
        } catch {
          // OBS-11 a-ignore: dual-write mirror to the local store after the server write succeeded; the mirror is a dev aid.
        }
      return row;
    } else {
      return await localInvitesStore.update(id, patch as any);
    }
  },

  /**
   * Record a resend (increment count, update timestamp)
   */
  async recordResend(id: string): Promise<ClientInvite | null> {
    if (backend === 'supabase') {
      return await supaInvitesStore.recordResend(id);
    }
    // Local doesn't track resends
    return await this.get(id);
  },

  /**
   * Revoke an invite
   */
  async revoke(id: string): Promise<ClientInvite | null> {
    if (backend === 'supabase') {
      return await supaInvitesStore.revoke(id);
    }
    return await this.update(id, { status: 'revoked' });
  },

  /**
   * Hard-delete an invite (wrong email, etc.). Distinct from revoke — this
   * removes the row entirely.
   */
  async remove(id: string): Promise<boolean> {
    if (backend === 'supabase') {
      return await supaInvitesStore.remove(id);
    }
    return await localInvitesStore.remove(id);
  },

  /**
   * Check rate limits
   */
  async checkRateLimits(coachId: string, email: string): Promise<InviteRateLimitResult> {
    if (backend === 'supabase') {
      const emailHash = await hashEmail(email);
      return await supaInvitesStore.checkRateLimits(coachId, emailHash);
    }
    // Local mode - no rate limits
    return { coachCount24h: 0, emailCount24h: 0, canSend: true };
  },

  /**
   * Check for duplicate invite (same coach + email within 24h)
   */
  async checkDedupe(coachId: string, email: string): Promise<InviteDedupeResult> {
    if (backend === 'supabase') {
      const emailHash = await hashEmail(email);
      return await supaInvitesStore.checkDedupe(coachId, emailHash);
    }
    // Local mode - no dedupe
    return { existingInviteId: null, lastSentAt: null, isDuplicate: false };
  },

  /**
   * Check if email is suppressed (bounced/complained)
   */
  async isEmailSuppressed(email: string): Promise<boolean> {
    if (backend === 'supabase') {
      const emailHash = await hashEmail(email);
      return await supaInvitesStore.isEmailSuppressed(emailHash);
    }
    return false;
  },

  /**
   * Redeem invite token
   */
  async redeemToken(token: string, email: string, userId: string, requestId: string): Promise<TokenRedemptionResult> {
    if (backend === 'supabase') {
      return await supaInvitesStore.redeemToken(token, email, userId, requestId);
    }
    // Local mode - simple validation
    return { success: true, inviteId: 'local', coachId: 'local' };
  },

  /**
   * Log audit event
   */
  async logAuditEvent(
    eventType: string,
    coachId: string,
    requestId: string,
    options: {
      inviteId?: string;
      emailHash?: string;
      ipHash?: string;
      metadata?: Record<string, unknown>;
    } = {},
  ): Promise<void> {
    if (backend === 'supabase') {
      await supaInvitesStore.logAuditEvent(eventType, coachId, requestId, options);
    }
    // Local mode - log to console in dev
    if (backend === 'local') {
      console.log(`[invites:audit] ${eventType}`, { coachId, requestId, ...options });
    }
  },
};

// Re-export utilities
export { hashEmail, hashToken, normalizeEmail };
